import type { FC, ReactNode } from 'react'
import { CalendarDays, CheckCircle2, FileText, Clock3, CalendarRange } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button, Loader } from '@/components/ui'
import { useEventStats } from '@/lib/api/hooks/event'

interface StatsCardProps {
	title: string
	value: string | number
	icon: ReactNode
	iconContainerClassName: string
	iconClassName: string
  description?: string
}

interface StatConfig {
	key: string;
	title: string;
	icon: LucideIcon;
	iconContainerClassName: string;
	iconClassName: string;
	description?: string;
}

const StatCard = ({
	title,
	value,
	icon,
	iconContainerClassName,
	iconClassName,
  description
}: StatsCardProps) => {
	return (
		<article className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="grid grid-cols-[auto_1fr] gap-4">
				<div className={`flex h-12 w-12 items-center justify-center rounded-md ${iconContainerClassName}`}>
					<span className={iconClassName}>{icon}</span>
				</div>
				<div className="flex flex-col gap-1">
					<p className="text-base font-medium text-gray-600">{title}</p>
					<p className="text-[2rem] leading-[1.1] font-semibold text-gray-900">{value}</p>
          {description && <p className="text-sm text-gray-500 ">{description}</p>}
				</div>
			</div>
		</article>
	)
}



const EventsStats: FC = () => {
	const { data: stats, isLoading, isError, refetch } = useEventStats()

  const STATS_CONFIG: StatConfig[] = [
	{
		key: 'total',
		title: 'Total Events',
		icon: CalendarDays,
		iconContainerClassName: 'bg-[#F8E8EB80]',
		iconClassName: 'text-[#8F2F44]',
    description: "All Time"
	},
	{
		key: 'totalPublished',
		title: 'Published',
		icon: CheckCircle2,
		iconContainerClassName: 'bg-[#E8F6EE80]',
		iconClassName: 'text-[#0F8A47]',
    description: `${stats?.publishedPercentageOfTotal ?? 0}% of total`
	},
	{
		key: 'totalDrafts',
		title: 'Drafts',
		icon: FileText,
		iconContainerClassName: 'bg-[#FDF3E680]',
		iconClassName: 'text-[#B86A00]',
    description: `${stats?.draftPercentageOfTotal ?? 0}% of total`
	},
	{
		key: 'totalUpcomingEvents',
		title: 'Upcoming',
		icon: Clock3,
		iconContainerClassName: 'bg-[#E8F0FE80]',
		iconClassName: 'text-[#1D4ED8]',
    description: `Next ${stats?.upcomingWindowDays ?? 0} days`
	},
	{
		key: 'totalPastEvents',
		title: 'Past Events',
		icon: CalendarRange,
		iconContainerClassName: 'bg-[#EDE9FE80]',
		iconClassName: 'text-[#6D28D9]',
    description: "Completed"
	},
]

	if (isError) {
		return (
			<div className="p-4 flex flex-col items-center justify-center w-full gap-4 sm:col-span-2 xl:col-span-5">
				<p>Error loading statistics.</p>
				<Button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded" loading={isLoading}>
					Retry
				</Button>
			</div>
		)
	}

	if (isLoading) {
		return (
			<div className="flex items-center justify-center">
				<Loader size="md" variant="wine" message="Loading statistics..." />
			</div>
		)
	}

	const values = {
    // "upcomingWindowDays": 90,
    //     "totalEvents": 14,
    //     "publishedEvents": 11,
    //     "publishedPercentageOfTotal": 78.57,
    //     "draftEvents": 3,
    //     "draftPercentageOfTotal": 21.43,
    //     "upcomingEvents": 1,
    //     "pastEvents": 4
  		total: stats?.totalEvents ?? 0,
		totalPublished: stats?.publishedEvents ?? 0,
		totalDrafts: stats?.draftEvents ?? 0,
		totalUpcomingEvents: stats?.upcomingEvents ?? 0,
		totalPastEvents: stats?.pastEvents ?? 0,
    publishedPercentageOfTotal: stats?.publishedPercentageOfTotal ?? 0,
    draftPercentageOfTotal: stats?.draftPercentageOfTotal ?? 0,
    upcomingWindowDays: stats?.upcomingWindowDays ?? 0,
	}

	return (
		<section className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 ">
			{STATS_CONFIG.map((config) => {
				const Icon = config.icon
				return (
					<StatCard
						key={config.key}
						title={config.title}
						value={values[config.key as keyof typeof values]}
						icon={<Icon className="w-6 h-6" />}
						iconContainerClassName={config.iconContainerClassName}
						iconClassName={config.iconClassName}
            description={config.description}
					/>
				)
			})}
		</section>
	)
}

export default EventsStats
